import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const useScrollReveal = () => {
  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll('.sipl-reveal, .sipl-reveal-ltr, .sipl-reveal-rtl, .sipl-reveal-ttb, .sipl-reveal-btt');
    if (isReduced) { elements.forEach((el) => el.classList.add('sipl-revealed')); return; }
    const observer = new IntersectionObserver((entries) => { entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('sipl-revealed'); observer.unobserve(entry.target); } }); }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });
    elements.forEach((el) => observer.observe(el));
    const check = () => { elements.forEach((el) => { const r = el.getBoundingClientRect(); if (r.top <= (window.innerHeight || document.documentElement.clientHeight) - 20) el.classList.add('sipl-revealed'); }); };
    check(); window.addEventListener('scroll', check, { passive: true });
    return () => { window.removeEventListener('scroll', check); observer.disconnect(); };
  }, []);
};

const DYNAMIC_MESSAGES = [
  { text: 'Custom Learning Management Systems (LMS) with interactive video lectures.', color: '#1a2f6e' },
  { text: 'Student Information Systems (SIS) & automated fee payment gateways.', color: '#0d9488' },
  { text: 'Online examination engines with auto-grading & PDF certificates.', color: '#4caf50' },
  { text: 'Dedicated portals for students, educators & parents.', color: '#f97316' },
];

const MARQUEE_ITEMS = [
  { label: 'Custom LMS Platforms', color: '#1a2f6e' },
  { label: 'Student Information System (SIS)', color: '#0d9488' },
  { label: 'Online Examination Engine', color: '#4caf50' },
  { label: 'Video Streaming & Lectures', color: '#f97316' },
  { label: 'Automated Fee Collection', color: '#1a2f6e' },
  { label: 'PDF Certificate Engine', color: '#0d9488' },
  { label: 'Parent-Teacher App', color: '#4caf50' },
  { label: 'Attendance Tracking', color: '#f97316' },
];

const CAPABILITIES = [
  {
    id: 'lms-platforms', num: '01', icon: '🎓',
    title: 'LMS Platforms',
    fullTitle: 'Custom Learning Management Systems (LMS)',
    subtitle: 'Course delivery, video lectures & student progress',
    desc: 'Engineer modern learning management platforms for universities, schools, and online course creators with interactive video players, assignments, and progress tracking.',
    deliverables: ['Interactive Course Builder & Structured Lesson Modules', 'Adaptive Video Player with Playback Speed & Quality Controls', 'Student Assignment Submissions & Teacher Feedback Portal', 'Automated Quiz Engine & Instant Score Calculation'],
    tags: ['Custom LMS', 'Video Lectures', 'Quiz Engine', 'Course Modules'],
    accent: '#1a2f6e',
  },
  {
    id: 'student-sis', num: '02', icon: '🏫',
    title: 'Campus & SIS',
    fullTitle: 'Student Information Systems (SIS) & Campus Management',
    subtitle: 'Admissions, student records, timetables & fees',
    desc: 'Unify campus administrative workflows into a single dashboard—from student admissions and attendance records to exam timetables and fee collection.',
    deliverables: ['Digital Student Admissions & Document Verification', 'Online School Fee Payment Gateway & Receipt Generation', 'Automated Exam Schedule & Class Timetable Generator', 'Biometric & RFID Student Attendance Tracking'],
    tags: ['SIS Portal', 'Fee Gateway', 'Admissions', 'Attendance'],
    accent: '#0d9488',
  },
  {
    id: 'online-exams', num: '03', icon: '✍️',
    title: 'Exams & Certificates',
    fullTitle: 'Online Examination & Automated Certification Engine',
    subtitle: 'Timed quizzes, auto-grading & PDF certificates',
    desc: 'Conduct secure online assessments with timed questionnaires, randomized question banks, automated grading rubrics, and instant PDF completion certificates.',
    deliverables: ['Timed Online Examination Portal with Question Shuffling', 'Automated Multiple-Choice & Objective Grading', 'Dynamic PDF Certificate Generator with QR Verification', 'Detailed Student Performance Analytics & Rank Sheets'],
    tags: ['Online Exams', 'Auto Grading', 'PDF Certificates', 'QR Verify'],
    accent: '#4caf50',
  },
  {
    id: 'portals-mobile', num: '04', icon: '📱',
    title: 'Parent & Mobile Apps',
    fullTitle: 'Parent-Teacher Mobile Portals & Communication Apps',
    subtitle: 'Real-time updates, grade books & announcements',
    desc: 'Keep parents informed about student academic progress, daily attendance, fee dues, school event announcements, and direct messaging with teachers.',
    deliverables: ['Native Mobile App for Parents & Students (iOS & Android)', 'Digital Student Gradebook & Report Card Viewer', 'Instant Push Notifications for School Announcements', 'Direct Parent-Teacher Messaging & Appointment Scheduler'],
    tags: ['Parent App', 'Digital Gradebook', 'Push Alerts', 'Teacher Chat'],
    accent: '#f97316',
  },
];

const AnimatedText = ({ phrases }) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => { const t = setInterval(() => { setVisible(false); setTimeout(() => { setIndex((p) => (p + 1) % phrases.length); setVisible(true); }, 300); }, 2600); return () => clearInterval(t); }, [phrases]);
  const cur = phrases[index];
  return <span style={{ display: 'inline-block', color: cur.color, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(-6px)', transition: 'opacity 0.45s ease, transform 0.45s ease, color 0.45s ease', fontWeight: 700 }}>{cur.text}</span>;
};

const EducationPage = () => {
  useScrollReveal();
  const [activeTabId, setActiveTabId] = useState('lms-platforms');
  const [isChangingTab, setIsChangingTab] = useState(false);
  useEffect(() => { document.title = 'Education & EdTech Solutions | Seikural Innovation'; window.scrollTo(0, 0); }, []);
  const handleTabChange = (id) => { if (id === activeTabId) return; setIsChangingTab(true); setTimeout(() => { setActiveTabId(id); setIsChangingTab(false); }, 220); };
  const activeCapability = CAPABILITIES.find((c) => c.id === activeTabId) || CAPABILITIES[0];

  return (
    <div className="sipl-home-page-wrapper text-dark overflow-hidden">
      <div style={{ height: 3, background: 'linear-gradient(90deg, #1a2f6e 0%, #0d9488 33%, #4caf50 66%, #f97316 100%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '1%', left: '-4%', width: 550, height: 550, background: 'radial-gradient(circle, rgba(26,47,110,0.045) 0%, transparent 70%)' }} />
      <div className="sipl-ambient-glow" style={{ position: 'absolute', pointerEvents: 'none', top: '30%', right: '-6%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(13,148,136,0.04) 0%, transparent 70%)' }} />

      <section className="py-5 border-bottom sipl-section-transparent sipl-hero" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container py-3">
          <div className="row align-items-center g-5">
            <div className="col-12 col-lg-7 sipl-reveal-ltr">
              <h1 className="display-5 fw-extrabold text-dark mb-2" style={{ letterSpacing: '-0.03em', lineHeight: 1.15 }}>Digital Platforms for Learning & Campus Management</h1>
              <div className="mb-3" style={{ minHeight: 36 }}><AnimatedText phrases={DYNAMIC_MESSAGES} /></div>
              <p className="text-muted mb-4" style={{ fontSize: '1.02rem', lineHeight: 1.65, maxWidth: 580 }}>We build scalable EdTech software solutions—from custom Learning Management Systems (LMS) and student portals to automated fee payment gateways and mobile parent apps.</p>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <Link to="/contact" className="btn px-4 py-2 rounded-3 fw-bold text-white shadow-sm sipl-arrow-link" style={{ backgroundColor: '#1a2f6e', border: 'none' }}><span>Discuss Education Project</span> <span>→</span></Link>
                <a href="#capabilities" className="btn btn-outline-secondary px-4 py-2 rounded-3 fw-semibold">Explore Capabilities</a>
              </div>
            </div>
            <div className="col-12 col-lg-5 sipl-reveal-rtl d-flex justify-content-center">
              <div className="p-4 rounded-4 bg-white shadow border text-center" style={{ width: '100%', maxWidth: 360 }}>
                <div className="mb-3 d-flex justify-content-center gap-2">{['#1a2f6e','#0d9488','#4caf50','#f97316'].map(c => <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: c, display: 'inline-block' }}></span>)}</div>
                <div className="fw-bold text-dark mb-1" style={{ fontSize: '1rem' }}>Education Software Domains</div>
                <div className="d-flex flex-column gap-2 mt-3">{CAPABILITIES.map(cap => (<div key={cap.id} className="d-flex align-items-center gap-2 p-2 rounded-3 bg-light border"><span style={{ fontSize: '1.1rem' }}>{cap.icon}</span><span className="small fw-medium text-dark">{cap.fullTitle}</span></div>))}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="sipl-marquee-container bg-white border-top border-bottom py-2">
        <div className="sipl-marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, idx) => (
            <span key={idx} className="badge bg-light text-dark border px-3 py-2 rounded-pill mx-1" style={{ fontSize: '0.82rem' }}>
              <span style={{ color: item.color, marginRight: 6 }}>●</span>{item.label}
            </span>
          ))}
        </div>
      </div>

      <section id="capabilities" className="py-5 bg-light">
        <div className="container py-4">
          <div className="text-center mb-4 sipl-reveal-ttb" style={{ maxWidth: 640, margin: '0 auto' }}>
            <span className="text-uppercase fw-bold small" style={{ color: '#1a2f6e', letterSpacing: '0.08em' }}>CAPABILITIES SHOWCASE</span>
            <h2 className="h3 fw-bold text-dark mb-2">What We Build for Education</h2>
            <p className="text-muted small mb-0">Select any domain to inspect technical deliverables and scope.</p>
          </div>
          <div className="d-flex justify-content-center mb-5 sipl-reveal-btt">
            <div className="p-1 bg-white rounded-pill shadow-sm border d-inline-flex align-items-center flex-wrap justify-content-center gap-1" style={{ borderColor: '#e2e8f0' }}>
              {CAPABILITIES.map((tab) => { const isActive = activeTabId === tab.id; return (
                <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                  className={`btn rounded-pill px-3 py-2 fw-bold text-nowrap d-inline-flex align-items-center gap-2 ${isActive ? 'shadow-sm' : 'border-0 bg-transparent text-secondary'}`}
                  style={{ backgroundColor: isActive ? tab.accent : 'transparent', color: isActive ? '#fff' : '#475569', fontSize: '0.86rem', transform: isActive ? 'scale(1.03)' : 'scale(1)', transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                  <span style={{ fontSize: '0.95rem' }}>{tab.icon}</span><span>{tab.title}</span>
                </button>
              ); })}
            </div>
          </div>
          <div className="mx-auto sipl-reveal-btt" style={{ maxWidth: 940 }}>
            <div className="card border shadow-sm rounded-4 p-4 p-md-5 bg-white sipl-card-hover" style={{ borderTop: `4px solid ${activeCapability.accent}`, opacity: isChangingTab ? 0 : 1, transform: isChangingTab ? 'translateY(18px) scale(0.99)' : 'translateY(0) scale(1)', transition: 'opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)' }}>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-4" style={{ width: 50, height: 50, backgroundColor: `${activeCapability.accent}18`, color: activeCapability.accent }}>{activeCapability.icon}</span>
                  <div>
                    <span className="badge px-2 py-1 rounded-pill fw-bold text-uppercase" style={{ backgroundColor: `${activeCapability.accent}18`, color: activeCapability.accent, fontSize: '0.72rem' }}>{activeCapability.num} / CORE DOMAIN</span>
                    <h3 className="h4 fw-bold text-dark mb-0 mt-1">{activeCapability.fullTitle}</h3>
                    <div className="text-muted small">{activeCapability.subtitle}</div>
                  </div>
                </div>
                <Link to="/contact" className="btn px-4 py-2 rounded-3 fw-bold text-white shadow-sm sipl-arrow-link" style={{ backgroundColor: activeCapability.accent, border: 'none', fontSize: '0.85rem' }}><span>Start Project</span> <span className="ms-1">→</span></Link>
              </div>
              <p className="text-muted small mb-4" style={{ lineHeight: 1.7, fontSize: '0.94rem' }}>{activeCapability.desc}</p>
              <div className="mb-4">
                <div className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em', color: activeCapability.accent }}>Key Technical Deliverables</div>
                <div className="row g-3">
                  {activeCapability.deliverables.map((del) => (
                    <div key={del} className="col-12 col-sm-6">
                      <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-light border h-100 sipl-card-hover">
                        <span className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0" style={{ width: 24, height: 24, backgroundColor: `${activeCapability.accent}18`, color: activeCapability.accent, fontSize: '0.78rem' }}>✓</span>
                        <span className="small text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{del}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-3 border-top d-flex flex-wrap align-items-center gap-2">
                <span className="text-uppercase fw-bold text-muted me-1" style={{ fontSize: '0.68rem', letterSpacing: '0.08em' }}>Stack:</span>
                {activeCapability.tags.map((tag) => (
                  <span key={tag} className="badge bg-light text-dark border px-3 py-1 rounded-pill fw-semibold" style={{ fontSize: '0.78rem' }}><span style={{ color: activeCapability.accent, marginRight: 5 }}>●</span>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-top sipl-reveal-btt d-flex align-items-center" style={{ minHeight: '40vh' }}>
        <div className="container text-center py-5">
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <h2 className="h4 fw-bold text-dark mb-2">Ready to Build Your Custom LMS or EdTech Portal?</h2>
            <p className="text-muted small mb-4">Connect with our EdTech software engineers to turn your learning platform vision into reality.</p>
            <div className="d-flex justify-content-center gap-3">
              <Link to="/contact" className="btn px-4 py-2 rounded-3 fw-bold text-white shadow-sm sipl-arrow-link" style={{ backgroundColor: '#1a2f6e' }}><span>Start Your Project</span> <span className="ms-1">→</span></Link>
              <Link to="/industries" className="btn btn-outline-secondary px-4 py-2 rounded-3 fw-semibold">All Industries</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationPage;
